import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { AuthenticatorEnrolment } from '@taqon/contracts';
import taqonLogo from '../../assets/taqon-logo.png';
import { QrCode } from '../../shared/ui/QrCode';
import { EASE, rise } from '../../shared/ui/motion';

/**
 * What a user needs to add IBMS to an authenticator app: the QR code and, for those who cannot
 * scan, the key to type. Shared by account setup and by sign-in for a user who has none yet.
 */
export function AuthenticatorDetails({ enrolment }: { enrolment: AuthenticatorEnrolment }) {
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <QrCode
          value={enrolment.otpauthUri}
          label="QR code to add TAQON Infinity to your authenticator app"
        />
      </Box>
      <Typography variant="body2" color="text.secondary">
        Cannot scan? Enter this key in the app, as a time-based key for {enrolment.accountName}:
      </Typography>
      <Box
        aria-label="Setup key"
        sx={{
          fontFamily: 'monospace',
          wordBreak: 'break-all',
          bgcolor: 'grey.100',
          p: 1.5,
          borderRadius: 1,
        }}
      >
        {enrolment.manualKey.replace(/(.{4})/g, '$1 ').trim()}
      </Box>
    </>
  );
}

/** The unauthenticated frame shared by sign-in and account setup. */
export function AuthCard({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <Box
      component="main"
      id="main"
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: { xs: 4, sm: 6 },
        bgcolor: 'background.default',
        backgroundImage: [
          'radial-gradient(900px 520px at 12% -10%, rgba(108, 203, 154, 0.26), transparent 70%)',
          'radial-gradient(800px 480px at 100% 110%, rgba(47, 49, 54, 0.12), transparent 70%)',
        ].join(', '),
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          maxWidth: 440,
          p: { xs: 3, sm: 5 },
          borderRadius: '24px',
          borderColor: 'rgba(47, 49, 54, 0.08)',
          boxShadow: '0 1px 2px rgba(47, 49, 54, 0.04), 0 24px 48px -16px rgba(47, 49, 54, 0.18)',
          animation: `${rise} 560ms ${EASE} backwards`,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          {/* The TAQON logo; the heading below names the product, so the image is decorative. */}
          <Box
            component="img"
            src={taqonLogo}
            alt=""
            sx={{ display: 'block', height: 64, width: 'auto', mx: 'auto', mb: 2 }}
          />
          <Typography
            component="h1"
            sx={{ fontSize: '1.75rem', fontWeight: 700, letterSpacing: '0.04em', lineHeight: 1.2 }}
          >
            TAQON{' '}
            <Box component="span" sx={{ color: 'primary.main' }}>
              INFINITY
            </Box>
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              color: 'primary.main',
              fontSize: '0.6875rem',
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              textWrap: 'balance',
            }}
          >
            Agentic AI Enterprise Resource Planning
          </Typography>
        </Box>

        <Typography component="h2" sx={{ fontSize: '1.125rem', fontWeight: 600, mb: 3 }}>
          {heading}
        </Typography>
        {children}
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 3 }}>
        © {new Date().getFullYear()} TAQON
      </Typography>
    </Box>
  );
}

/** A password field with a show/hide toggle. */
export function PasswordField(props: TextFieldProps) {
  const [shown, setShown] = useState(false);
  return (
    <TextField
      {...props}
      type={shown ? 'text' : 'password'}
      fullWidth
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                edge="end"
                aria-label={shown ? 'Hide password' : 'Show password'}
                onClick={() => {
                  setShown((value) => !value);
                }}
              >
                {shown ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

/** The 6-digit authenticator code: numeric keypad on phones, one-time-code autofill. */
export function CodeField(props: TextFieldProps & { onCode: (code: string) => void }) {
  const { onCode, ...rest } = props;
  return (
    <TextField
      label="Authenticator code"
      autoComplete="one-time-code"
      required
      fullWidth
      {...rest}
      onChange={(e) => {
        onCode(e.target.value.replace(/\D/g, '').slice(0, 6));
      }}
      slotProps={{ htmlInput: { inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 } }}
    />
  );
}

/**
 * Recovery codes, shown once (FRD-USR-027): each works once, in place of an authenticator code.
 * Copy and download keep them out of the screen's history only as far as the user chooses.
 */
export function RecoveryCodes({ codes }: { codes: readonly string[] }) {
  const [copied, setCopied] = useState(false);
  const text = codes.join('\n');
  return (
    <Stack spacing={2}>
      <Alert severity="warning" variant="outlined">
        Save these recovery codes somewhere safe, away from your phone. Each works once if you lose
        your authenticator. They will not be shown again.
      </Alert>
      <Box
        component="ul"
        aria-label="Recovery codes"
        sx={{
          m: 0,
          p: 2,
          listStyle: 'none',
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 1,
          fontFamily: 'monospace',
          fontSize: '1rem',
          bgcolor: 'grey.100',
          borderRadius: 1,
        }}
      >
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </Box>
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          onClick={() => {
            void navigator.clipboard.writeText(text).then(() => {
              setCopied(true);
            });
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            const url = URL.createObjectURL(new Blob([`${text}\n`], { type: 'text/plain' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = 'taqon-ibms-recovery-codes.txt';
            link.click();
            URL.revokeObjectURL(url);
          }}
        >
          Download
        </Button>
      </Stack>
    </Stack>
  );
}

/** Material Design icon paths (Apache 2.0), inline so the SPA needs no icon package here. */
function Icon({ children }: { children: ReactNode }) {
  return <SvgIcon fontSize="small">{children}</SvgIcon>;
}

function VisibilityIcon() {
  return (
    <Icon>
      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
    </Icon>
  );
}

function VisibilityOffIcon() {
  return (
    <Icon>
      <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" />
    </Icon>
  );
}
